import React, { createContext, useContext, ReactNode, useEffect, useMemo, useState, useCallback } from 'react';
import { useForm, UseFormReturn, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useBusinessOperations } from '@/context/schema/useBusinessOperations';
import { useFirebase } from '@/context/FirebaseProvider';
import { Business, User as UserData } from '@/schemas';
import { START_OF_DAY, END_OF_DAY_UI, END_OF_DAY_SAVE } from '@/constants/time';

// Default business hours - Uses 'from' and 'to'
const defaultBusinessHours = {
  monday: { open: true, from: '09:00 AM', to: '05:00 PM', allDay: false },
  tuesday: { open: true, from: '09:00 AM', to: '05:00 PM', allDay: false },
  wednesday: { open: true, from: '09:00 AM', to: '05:00 PM', allDay: false },
  thursday: { open: true, from: '09:00 AM', to: '05:00 PM', allDay: false },
  friday: { open: true, from: '09:00 AM', to: '05:00 PM', allDay: false },
  saturday: { open: false, from: '', to: '', allDay: false },
  sunday: { open: false, from: '', to: '', allDay: false },
};

// Zod Schema for LeadDeliverySettings - Form's internal data structure
const leadDeliverySettingsSchema = z.object({
  phone: z.string().min(1, "Phone number is required."),
  email: z.string().email("Invalid email address.").min(1, "Email is required."),
  customBusinessHours: z.boolean(),
  businessHours: z.record(
    z.object({
      open: z.boolean(),
      from: z.string().optional(),
      to: z.string().optional(),
      allDay: z.boolean().optional(),
    })
  ).optional(),
}).superRefine((data, ctx) => {
  if (data.customBusinessHours) {
    if (!data.businessHours || Object.keys(data.businessHours).length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["businessHours"], message: "Business hours details are required." });
      return; 
    }
    let atLeastOneDayOpen = false;
    for (const day of Object.keys(defaultBusinessHours)) {
      const dayHours = data.businessHours[day];
      if (dayHours?.open) {
        atLeastOneDayOpen = true;
        if (!dayHours.allDay) {
          if (!dayHours.from || dayHours.from.trim() === "") {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [`businessHours.${day}.from`], message: "Required" });
          }
          if (!dayHours.to || dayHours.to.trim() === "") {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [`businessHours.${day}.to`], message: "Required" });
          }
        }
      }
    }
    if (!atLeastOneDayOpen) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["customBusinessHours"], message: "At least one day must be open." });
    }
  }
});

export type LeadDeliverySettings = z.infer<typeof leadDeliverySettingsSchema>;

type FirestoreLeadDeliveryData = {
  phone?: string;
  phones?: string[];
  email?: string;
  emails?: string[];
  hasBeenSaved?: boolean;
  customBusinessHours?: boolean;
  businessHours?: Record<string, { open: boolean; openTime?: string; closeTime?: string; from?: string; to?: string; allDay?: boolean }>;
  phoneDelivery?: boolean;
  emailDelivery?: boolean;
};

interface LeadDeliveryContextType extends Omit<UseFormReturn<LeadDeliverySettings>, 'reset'> {
  businessId: string;
  onSubmit: (data: LeadDeliverySettings) => Promise<void>;
  isLoading: boolean;
  resetForm: (values?: LeadDeliverySettings) => void;
  formErrors: FieldErrors<LeadDeliverySettings>;
}

// Memoized function to create default form values
const createDefaultFormValues = (
  currentBusiness?: Business,
  currentUserData?: UserData
): LeadDeliverySettings => ({
  phone: currentBusiness?.phone || currentUserData?.phone || '',
  email: currentBusiness?.email || currentUserData?.email || '',
  customBusinessHours: true,
  businessHours: JSON.parse(JSON.stringify(defaultBusinessHours)),
});

const LeadDeliveryContext = createContext<LeadDeliveryContextType | undefined>(undefined);

export const useLeadDeliveryFormContext = () => {
  const context = useContext(LeadDeliveryContext);
  if (!context) throw new Error('useLeadDeliveryFormContext must be used within a LeadDeliveryStateProvider');
  return context;
};

interface LeadDeliveryStateProviderProps {
  businessId: string;
  children: ReactNode;
  onSaveSuccess?: () => void;
}

export const LeadDeliveryStateProvider: React.FC<LeadDeliveryStateProviderProps> = ({
  businessId,
  children,
  onSaveSuccess,
}) => {
  const { userData, businesses, isLoading: isFirebaseLoading } = useFirebase();
  const business = useMemo(() => businesses.find(b => b.id === businessId), [businesses, businessId]);
  const { saveGoCampaignSection, isLoading: isSaving } = useBusinessOperations(userData);

  // Get initial settings from Firestore or defaults
  const initialSettings = useMemo(() => {
    const firestoreLeadDelivery = business?.gocampaign?.leadDelivery as FirestoreLeadDeliveryData | undefined;
    
    // Check if we have relevant data in Firestore
    const hasRelevantFirestoreData = firestoreLeadDelivery && Object.keys(firestoreLeadDelivery).length > 0 &&
                                   (firestoreLeadDelivery.phone || 
                                    (firestoreLeadDelivery.phones && firestoreLeadDelivery.phones.length > 0) ||
                                    firestoreLeadDelivery.email || 
                                    (firestoreLeadDelivery.emails && firestoreLeadDelivery.emails.length > 0) ||
                                    typeof firestoreLeadDelivery.customBusinessHours === 'boolean' ||
                                    firestoreLeadDelivery.businessHours);

    if (hasRelevantFirestoreData) {
      // Map business hours from Firestore format to our format
      const mappedBusinessHours = { ...JSON.parse(JSON.stringify(defaultBusinessHours)) };
      
      if (firestoreLeadDelivery.businessHours) {
        for (const dayKey in mappedBusinessHours) {
          const fsDay = firestoreLeadDelivery.businessHours[dayKey];
          if (fsDay) {
            const from = fsDay.from || fsDay.openTime || mappedBusinessHours[dayKey].from;
            let to = fsDay.to || fsDay.closeTime || mappedBusinessHours[dayKey].to;
            const isAllDayTime = to.startsWith('11:59');
            if (isAllDayTime) {
              to = END_OF_DAY_UI;
            }
            mappedBusinessHours[dayKey] = {
              open: fsDay.open ?? mappedBusinessHours[dayKey].open,
              from: isAllDayTime ? START_OF_DAY : from,
              to,
              allDay:
                typeof fsDay.allDay === 'boolean'
                  ? fsDay.allDay
                  : from.startsWith(START_OF_DAY) && isAllDayTime,
            };
          }
        }
      }

      // Get phone and email from Firestore or business
      const initialPhone =
        firestoreLeadDelivery.phone ||
        (firestoreLeadDelivery.phones && firestoreLeadDelivery.phones.length > 0
          ? firestoreLeadDelivery.phones[0]
          : business?.phone || userData?.phone || '');

      const initialEmail =
        firestoreLeadDelivery.email ||
        (firestoreLeadDelivery.emails && firestoreLeadDelivery.emails.length > 0
          ? firestoreLeadDelivery.emails[0]
          : business?.email || userData?.email || '');
      
      return {
        phone: String(initialPhone || ''),
        email: String(initialEmail || ''),
        customBusinessHours: firestoreLeadDelivery.customBusinessHours ?? true,
        businessHours: mappedBusinessHours,
      };
    } else {
      // No Firestore data, use defaults
      return createDefaultFormValues(business, userData);
    }
  }, [business, userData]);

  // Initialize form with React Hook Form
  const formMethods = useForm<LeadDeliverySettings>({
    resolver: zodResolver(leadDeliverySettingsSchema),
    defaultValues: initialSettings,
    mode: 'onChange',
  });

  const { reset, trigger, formState: { isDirty, errors } } = formMethods;

  // Reset form when business or user data changes
useEffect(() => {
  reset(initialSettings);
  trigger();
}, [reset, trigger, initialSettings]);

  // Submit handler
  const onSubmit = useCallback(async (formData: LeadDeliverySettings) => {
    // Transform business hours to Firestore format
    const businessHoursForFirestore: Record<string, {open: boolean; openTime: string; closeTime: string; allDay?: boolean}> = {};
    
    if (formData.customBusinessHours && formData.businessHours) {
      for (const dayKey in formData.businessHours) {
        const day = formData.businessHours[dayKey];
        businessHoursForFirestore[dayKey] = {
          open: day.open,
          openTime: day.allDay ? START_OF_DAY : day.from || '',
          closeTime: day.allDay ? END_OF_DAY_SAVE : day.to || '',
          allDay: day.allDay,
        };
      }
    }

    // Prepare data for Firestore
    const dataToSaveToFirestore: FirestoreLeadDeliveryData = {
      phone: formData.phone,
      email: formData.email,
      phones: [formData.phone],
      emails: [formData.email],
      hasBeenSaved: true,
      customBusinessHours: formData.customBusinessHours,
      businessHours: formData.customBusinessHours ? businessHoursForFirestore : {},
    };
    
    try {
      const success = await saveGoCampaignSection(businessId, 'leadDelivery', dataToSaveToFirestore as Record<string, any>);
      if (success) {
        reset(formData);
        if (onSaveSuccess) onSaveSuccess();
      }
    } catch (error) {
      // Error handling could be added here
    }
  }, [businessId, reset, saveGoCampaignSection, onSaveSuccess]);

  // Reset form with new values
  const resetForm = useCallback((values?: LeadDeliverySettings) => {
    reset(values || initialSettings);
  }, [reset, initialSettings]);

  // Create context value
  const contextValue = useMemo<LeadDeliveryContextType>(() => ({
    ...formMethods,
    businessId,
    onSubmit,
    isLoading: isSaving || isFirebaseLoading,
    resetForm,
    formErrors: errors,
  }), [
    formMethods, 
    businessId, 
    onSubmit, 
    isSaving, 
    isFirebaseLoading, 
    resetForm, 
    errors
  ]);

  return (
    <LeadDeliveryContext.Provider value={contextValue}>
      {children}
    </LeadDeliveryContext.Provider>
  );
};
